<template>
	<div class="multi-endpoint-notifications">
		<!-- Stack multiple notifications for different endpoints -->
		<v-slide-y-transition group>
			<v-alert
				v-for="notification in visibleNotifications"
				:key="notification.endpoint"
				prominent
				:type="notification.isLockedOut ? 'error' : 'warning'"
				elevation="4"
				class="endpoint-rate-limit-notification mb-2"
				:color="notification.isLockedOut ? 'red darken-2' : 'orange darken-1'"
				dark
				dismissible
				@input="dismissEndpointNotification(notification.endpoint, $event)"
			>
				<div class="text-h6 mb-2">
					<v-icon left>
						{{ notification.isLockedOut ? "mdi-lock-alert" : "mdi-clock-alert-outline" }}
					</v-icon>
					{{ notification.displayName }} Rate Limited
				</div>
				<div class="text-body-1 mb-2">
					<span v-if="notification.isLockedOut">
						Too many requests detected. Temporarily locked for
						<strong>{{ notification.lockoutDuration }} seconds</strong>
						.
					</span>
					<span v-else>
						You've exceeded the rate limit. Please wait
						<strong>{{ formatTime(notification.remainingTime) }}</strong>
						before trying again.
					</span>
				</div>

				<!-- Show violated limit if available -->
				<div v-if="notification.violatedLimit" class="text-caption mb-2">
					<v-icon small left>mdi-information</v-icon>
					Violated limit: {{ notification.violatedLimit }}
				</div>

				<!-- Progress bar showing countdown -->
				<v-progress-linear
					:value="getProgressPercentage(notification)"
					color="white"
					background-color="orange darken-3"
					height="25"
					rounded
					class="mt-2"
				>
					<template v-slot:default>
						<strong>{{ formatTime(notification.remainingTime) }}</strong>
					</template>
				</v-progress-linear>

				<!-- Queue status if requests are queued for this endpoint -->
				<div
					v-if="notification.queuedRequests && notification.queuedRequests.length > 0"
					class="text-caption mt-2"
				>
					<v-icon small left>mdi-playlist-play</v-icon>
					{{ notification.queuedRequests.length }} request{{
						notification.queuedRequests.length !== 1 ? "s" : ""
					}}
					queued for retry
				</div>
			</v-alert>
		</v-slide-y-transition>

		<!-- Summary chip if too many notifications -->
		<v-fade-transition>
			<v-chip
				v-if="hiddenNotificationCount > 0"
				color="orange"
				text-color="white"
				small
				class="mt-2 hidden-notifications-chip"
				@click="showAllNotifications = !showAllNotifications"
			>
				<v-icon left small>mdi-alert</v-icon>
				+{{ hiddenNotificationCount }} more rate limit{{ hiddenNotificationCount > 1 ? "s" : "" }}
				<v-icon right small>{{ showAllNotifications ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
			</v-chip>
		</v-fade-transition>

		<!-- Warning chips for endpoints approaching limits -->
		<div v-if="endpointsApproachingLimit.length > 0" class="approaching-limit-chips mt-2">
			<v-chip
				v-for="endpoint in endpointsApproachingLimit"
				:key="`warning-${endpoint.endpoint}`"
				color="blue-grey"
				text-color="white"
				small
				class="mr-2 mb-2"
				close
				@click:close="dismissWarning(endpoint.endpoint)"
			>
				<v-icon left small>mdi-alert-circle-outline</v-icon>
				{{ endpoint.displayName }}: {{ endpoint.requestsRemaining }} left
			</v-chip>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { formatRemainingTime } from "@/utils/rateLimitUtils";

export default {
	name: "MultiEndpointRateLimitNotification",

	data() {
		return {
			dismissedEndpoints: {},
			dismissedWarnings: {},
			showAllNotifications: false,
			maxVisibleNotifications: 3,
		};
	},

	computed: {
		...mapGetters({
			allRateLimitedEndpoints: "rateLimiting/allRateLimitedEndpoints",
			visibleRateLimitedEndpoints: "rateLimiting/visibleRateLimitedEndpoints",
			getEndpointData: "rateLimiting/getEndpointRateLimitData",
			getEndpointDisplayName: "rateLimiting/getEndpointDisplayName",
			isEndpointRateLimited: "rateLimiting/isEndpointRateLimited",
			isAnyEndpointRateLimited: "rateLimiting/isAnyEndpointRateLimited",
			isExempt: "isUserRateLimitExempt",
		}),

		visibleNotifications() {
			// If user is exempt from rate limiting, don't show any notifications
			if (this.isExempt) {
				return [];
			}
			// Get endpoints that are rate limited and not dismissed
			const endpoints = this.visibleRateLimitedEndpoints
				.filter((endpoint) => {
					// Check if endpoint has been re-triggered after dismissal
					const data = this.getEndpointData(endpoint);
					if (data && this.dismissedEndpoints[endpoint]) {
						// If lastTrigger is newer than our dismissal, clear the dismissal
						const dismissalTime = this.dismissedEndpoints[endpoint];
						if (typeof dismissalTime === "number" && data.lastTrigger > dismissalTime) {
							this.$delete(this.dismissedEndpoints, endpoint);
							return true;
						}
					}
					return !this.dismissedEndpoints[endpoint];
				})
				.map((endpoint) => {
					const data = this.getEndpointData(endpoint);
					return {
						endpoint,
						displayName: this.getEndpointDisplayName(endpoint),
						...data,
					};
				});

			// Limit to max visible unless user wants to see all
			const limit = this.showAllNotifications ? endpoints.length : this.maxVisibleNotifications;
			return endpoints.slice(0, limit);
		},

		hiddenNotificationCount() {
			const total = this.visibleRateLimitedEndpoints.filter((endpoint) => {
				// Check if endpoint has been re-triggered after dismissal
				const data = this.getEndpointData(endpoint);
				if (data && this.dismissedEndpoints[endpoint]) {
					const dismissalTime = this.dismissedEndpoints[endpoint];
					if (typeof dismissalTime === "number" && data.lastTrigger > dismissalTime) {
						return true; // Count as not dismissed since it was re-triggered
					}
				}
				return !this.dismissedEndpoints[endpoint];
			}).length;

			if (this.showAllNotifications) {
				return 0;
			}

			return Math.max(0, total - this.maxVisibleNotifications);
		},

		endpointsApproachingLimit() {
			// Get endpoints that have low remaining requests but aren't rate limited yet
			const endpoints = [];

			// This would need to be implemented based on tracking request counts
			// For now, return empty array
			return endpoints;
		},
	},

	methods: {
		...mapActions({
			dismissEndpoint: "rateLimiting/dismissEndpointNotification",
			clearEndpointRateLimit: "rateLimiting/clearEndpointRateLimit",
		}),

		dismissEndpointNotification(endpoint, value) {
			if (!value) {
				// User dismissed the notification - store timestamp for comparison
				this.$set(this.dismissedEndpoints, endpoint, Date.now());
				this.dismissEndpoint(endpoint);

				// Reset dismissal after the rate limit expires
				const data = this.getEndpointData(endpoint);
				if (data && data.remainingTime > 0) {
					setTimeout(() => {
						this.$delete(this.dismissedEndpoints, endpoint);
					}, data.remainingTime * 1000);
				}
			}
		},

		dismissWarning(endpoint) {
			this.$set(this.dismissedWarnings, endpoint, true);
			// Reset after 5 minutes
			setTimeout(() => {
				this.$delete(this.dismissedWarnings, endpoint);
			}, 5 * 60 * 1000);
		},

		formatTime(seconds) {
			return formatRemainingTime(seconds);
		},

		getProgressPercentage(notification) {
			if (!notification.retryAfter || notification.retryAfter === 0) return 100;
			return Math.max(
				0,
				Math.min(
					100,
					((notification.retryAfter - notification.remainingTime) / notification.retryAfter) * 100
				)
			);
		},

		handleEndpointRateLimited(event) {
			// Reset dismissal for this endpoint when a new rate limit occurs
			const { endpoint } = event;
			if (this.dismissedEndpoints[endpoint]) {
				this.$delete(this.dismissedEndpoints, endpoint);
			}
		},
	},

	watch: {
		// Reset dismissed endpoints when they're no longer rate limited
		allRateLimitedEndpoints(newEndpoints) {
			Object.keys(this.dismissedEndpoints).forEach((endpoint) => {
				if (!newEndpoints.includes(endpoint)) {
					this.$delete(this.dismissedEndpoints, endpoint);
				}
			});
		},

		// Auto-collapse if only a few notifications remain
		hiddenNotificationCount(newCount) {
			if (newCount === 0 && this.showAllNotifications) {
				this.showAllNotifications = false;
			}
		},
	},

	mounted() {
		// Listen for endpoint-specific rate limit events
		this.$rateLimitBus.$on("endpoint-rate-limited", this.handleEndpointRateLimited);
	},

	beforeDestroy() {
		// Clean up event listener
		this.$rateLimitBus.$off("endpoint-rate-limited", this.handleEndpointRateLimited);
	},
};
</script>

<style lang="scss" scoped>
.multi-endpoint-notifications {
	position: fixed;
	top: 64px; // Below navbar
	left: 16px;
	right: 16px;
	z-index: 99999;
	max-width: 1000px;
	margin: 0 auto;
	pointer-events: none; // Allow clicks to pass through container

	@media (max-width: 600px) {
		top: 56px; // Adjust for mobile navbar
		left: 8px;
		right: 8px;
	}
}

.endpoint-rate-limit-notification {
	pointer-events: auto; // But capture clicks on alerts
	transition: all 0.3s ease;

	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
	}
}

.hidden-notifications-chip {
	pointer-events: auto;
	cursor: pointer;
	animation: pulse 2s infinite;

	&:hover {
		animation: none;
		transform: scale(1.05);
	}
}

.approaching-limit-chips {
	position: fixed;
	bottom: 24px;
	left: 24px;
	z-index: 9997;
	pointer-events: auto;

	@media (max-width: 600px) {
		bottom: 70px; // Above mobile navigation if present
		left: 8px;
		right: 8px;
	}

	.v-chip {
		animation: subtle-pulse 3s infinite;
	}
}

@keyframes pulse {
	0% {
		transform: scale(1);
		opacity: 1;
	}
	50% {
		transform: scale(1.05);
		opacity: 0.9;
	}
	100% {
		transform: scale(1);
		opacity: 1;
	}
}

@keyframes subtle-pulse {
	0%,
	100% {
		opacity: 0.8;
	}
	50% {
		opacity: 1;
	}
}

// Dark theme adjustments
.theme--dark {
	.endpoint-rate-limit-notification {
		.v-alert__content {
			color: #fff !important;
		}
	}
}

// Animation for queue status icon
.v-icon {
	&.mdi-playlist-play {
		animation: bounce 1s infinite;
	}
}

@keyframes bounce {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-3px);
	}
}

// Stacking animation when multiple notifications
.v-slide-y-transition-enter-active,
.v-slide-y-transition-leave-active {
	transition: all 0.3s ease;
}

.v-slide-y-transition-enter {
	transform: translateY(-20px);
	opacity: 0;
}

.v-slide-y-transition-leave-to {
	transform: translateY(-20px);
	opacity: 0;
}
</style>
